import React from 'react';
import { CarriedAdjustment, StatementLoan } from '../../types/commission';
import { formatCount, formatUsd, sumCents } from '../../utils/format';
import { Money } from '../sf/Money';
import { StoredReference } from '../sf/StoredReference';
interface StatementSummaryProps {
  loans: StatementLoan[];
  adjustments: CarriedAdjustment[];
  units: number;
  volume: number;
  adjustedVolume: number;
  period: string;
}
export function StatementSummary({
  loans,
  adjustments,
  units,
  volume,
  adjustedVolume,
  period
}: StatementSummaryProps) {
  const allLines = loans.flatMap(loan => loan.lines);
  const base = sumCents(allLines.filter(line => line.component === 'Base').map(line => line.adjustedAmount));
  const bonus = sumCents(allLines.filter(line => line.component === 'Monthly Bonus').map(line => line.adjustedAmount));
  const adjustmentTotal = sumCents(adjustments.map(item => item.amount));
  const storedTotal = sumCents([...allLines.map(line => line.payAmount), adjustmentTotal]);
  const adjustedTotal = sumCents([...allLines.map(line => line.adjustedAmount), adjustmentTotal]);
  const lineCount = allLines.length + adjustments.length;
  const stats = [{
    label: 'Base (adjusted)',
    value: <Money value={base} data-id="element-1068" data-mp="585ooibl6q6r" />
  }, {
    label: 'Monthly Bonus (adjusted)',
    value: <Money value={bonus} data-id="element-1069" data-mp="1851a86if4nk" />
  }, {
    label: 'Adjustments',
    value: <Money value={adjustmentTotal} signed data-id="element-1070" data-mp="19ohjys61fpc" />
  }, {
    label: 'Closed Loans (month)',
    value: <span className="tabular-nums" data-id="element-1071" data-mp="3sf7sgzmclaxs">{units}</span>
  }, {
    label: 'Adjusted Volume',
    value: <span className="tabular-nums" data-id="element-1072" data-mp="3nllqc1dt8pg8">{formatUsd(adjustedVolume)}</span>
  }, {
    label: 'Volume Funded',
    value: <span className="tabular-nums" data-id="element-1073" data-mp="3iomvfgmboegz">{formatUsd(volume)}</span>
  }];
  return <section aria-label="Commission totals" className="grid grid-cols-1 overflow-hidden rounded border border-sf-border bg-white shadow-card lg:grid-cols-[420px_minmax(0,1fr)]" data-id="element-1074" data-mp="3dq1zhte3sgyc">
      <div className="border-b border-sf-border px-5 py-4 lg:border-b-0 lg:border-r" data-id="element-1075" data-mp="34nspr5qnsrm0">
        <p className="text-xs font-semibold text-sf-weak" data-id="element-1076" data-mp="2v0yf71wmgty1">Total Commission · {period}</p>
        <p className="mt-2 text-[11px] text-sf-weak" data-id="element-1077" data-mp="2z2bnrjr3nb8n">Adjusted · Adjusted Loan Amount × BPS — pays</p>
        <p className="mt-1 text-[32px] font-bold leading-none tabular-nums" data-id="element-1078" data-mp="2l2iqt6ozbys8">{formatUsd(adjustedTotal)}</p>
        <p className="mt-2" data-id="element-1079" data-mp="2q1dfgp9mohk9">
          <StoredReference value={storedTotal} data-id="element-1080" data-mp="2q2oj8p32ppbd" />
        </p>
        <p className="mt-3 text-xs text-sf-weak" data-id="element-1081" data-mp="3dr7qd1adzw1v">Sum of {formatCount(lineCount)} commission lines.</p>
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-3" data-id="element-1082" data-mp="3iqsr9h94aeh8">
        {stats.map((stat, index) => <div key={stat.label} className={`px-5 py-3.5 ${index < 3 ? 'md:border-b md:border-sf-border' : ''} ${index % 3 !== 0 ? 'md:border-l md:border-sf-border' : ''}`} data-id="element-1083" data-mp="341obs7tpioct" data-mpi={index}>
            <dt className="text-xs text-sf-weak" data-id="element-1084" data-mp="38y4vmptvigk3">{stat.label}</dt>
            <dd className="mt-0.5 text-lg font-semibold" data-id="element-1085" data-mp="j3bvp4vl8uw">{stat.value}</dd>
          </div>)}
      </dl>
    </section>;
}